import { CdkConnectedOverlay } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { MatSelectionList } from '@angular/material/list';
import { Store } from '@ngrx/store';
import { AppService } from '@src/suite/app.service';
import { EnpsComponent } from '@src/suite/common/components/base/enps-component';
import { OPTION_BLANK } from '@src/suite/common/enums/constants';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { selectI18N, selectTranslatedKey } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { i18nKeys } from '@src/suite/common/utils/i18n.default';

@Component({
    selector: 'st-story-icon-list',
    templateUrl: './story-icon-list.html',
    styleUrls: ['./story-icon-list.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'st-story-icon-list',
    },
})
export class StoryIconListComponent extends EnpsComponent {
    store = inject(Store);
    globalTablesService = inject(GlobalTablesService);
    appService = inject(AppService);

    cardDisabled = input(false);
    disabled = input(false);
    topicEditable = input(true);
    maxItemsToShow = input(5);
    shouldShowAddButton = input(true);
    parentRecord = input<StorytellingTopicRecord>(null);
    value = input<StorytellingStoryRecord[]>([]);
    templates = input<StorytellingStoryRecord[]>([]);
    showAddButton = input(true);

    overlayEl = viewChild(CdkConnectedOverlay);

    itemClicked = output<StorytellingStoryRecord>();
    createStoryEvent = output<{
        templateProgramId: string | null;
        templateGuids: string[];
    }>();

    objectType = ObjectTypes.SCRIPT;

    isPanelOpen = signal(false);

    storiesTypes = computed(() => {
        return this.globalTablesService.getListItemsForField(this.appService.PSTFieldNames.PSTStoryOutputType);
    });

    isRTL = computed(() => this.globalTablesService.getRtl());

    extraItemsCount = computed(() => this.value().length - this.maxItemsToShow());
    shouldShowExtraItems = computed(() => this.extraItemsCount() > 0);

    i18n$ = this.store.select(selectI18N);

    itemsToShow = computed(() => {
        const length = this.shouldShowExtraItems() ? this.maxItemsToShow() : this.value().length;
        return this.value().slice(0, length);
    });

    isCompactView = computed(() => this.maxItemsToShow() === 0);
    hasButtonLabel = computed(() => this.value()?.length === 0 && !this.isCompactView());
    addStoryLabel = computed(() => (this.hasButtonLabel() ? `${this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebAddStoryLabel))()}` : ''));

    panelAttached() {
        const overlayElement = this.overlayEl()?.overlayRef?.overlayElement;
        if (overlayElement) {
            overlayElement.addEventListener('mouseleave', () => {
                this.isPanelOpen.set(false);
            });
        }
    }

    onMouseOut(event: MouseEvent) {
        if (!(event.relatedTarget as HTMLElement)?.classList?.contains('st-story-icon-list__overlay')) {
            this.isPanelOpen.set(false);
        }
    }

    openOverlay() {
        if (this.cardDisabled()) return;
        this.isPanelOpen.set(true);
    }

    createStories(stories: MatSelectionList) {
        if (stories.selectedOptions.selected.length === 0) {
            return;
        }

        let templateProgramId: string | null = null;
        const selectedTemplates: StorytellingStoryRecord[] = stories.selectedOptions.selected.map(item => item.value);
        const templateGuids: string[] = selectedTemplates.map(template => template.Guid);
        selectedTemplates
            .filter(template => template.Guid !== OPTION_BLANK)
            .forEach(template => {
                if (template && template.Path && template.Path.fullPath) {
                    templateProgramId = template.Path.fullPath;
                }
            });

        try {
            this.createStoryEvent.emit({ templateProgramId: templateProgramId, templateGuids: templateGuids });
        } catch (ex) {
            console.log(ex);
        }
        stories.deselectAll();
    }

    onItemClicked(story: StorytellingStoryRecord) {
        this.itemClicked.emit(story);
        this.isPanelOpen.set(false);
    }
}
